import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';
import { createFetch, createHttpsAgent } from '../../utils/http';
import { AMPLIENCE_HTTPS_OPTIONS, AMPLIENCE_REQUEST_TIMEOUT } from './config';

/**
 * Connection-pooled fetch factory for Amplience
 * Uses Amplience-specific HTTPS agent settings for optimal performance
 */
const baseFetch = createFetch(AMPLIENCE_HTTPS_OPTIONS);

/**
 * Fetch implementation for Amplience content delivery requests
 *
 * @param url - The Amplience URL to fetch
 * @param options - Request options, including custom timeout if needed
 * @returns Promise resolving to the response
 */
export const amplienceFetch = (url: string, options = {}) =>
  baseFetch(url, {
    timeout: AMPLIENCE_REQUEST_TIMEOUT, // 8s
    ...options, // Allow override
  });

/**
 * Axios Adapter for Amplience SDK with Connection Pooling
 *
 * HOW IT WORKS:
 * 1. ContentClient SDK makes request → Adapter receives Axios config
 * 2. Injects custom httpsAgent with connection pooling settings
 * 3. Sets Amplience-specific timeout (8s default)
 * 4. Returns response in Axios-compatible format
 *
 * @param config - Axios request configuration from SDK
 * @returns Promise resolving to Axios-compatible response
 */
export const amplienceAxiosAdapter = async (config: AxiosRequestConfig) => {
  const enhancedConfig = {
    ...config,
    httpsAgent: createHttpsAgent(AMPLIENCE_HTTPS_OPTIONS),
    timeout: config.timeout || AMPLIENCE_REQUEST_TIMEOUT,
  };

  // Use axios.request directly — no need to fetch adapter manually
  return axios.request(enhancedConfig);
};
